import { toCanonicalPathname } from '@shared/utils';
import type { Metadata } from 'next';
import { notFound } from 'next/navigation';
import { cache } from 'react';
import { getPromoLandingPageData as getPromoLandingPageDataOriginal } from './api';
import type { MultiofferLandingPageData } from './templates/MultiofferLandingPage/types.ts';
import type { NetworkPromoLandingPageData } from './templates/NetworkPromoLandingPage/types.ts';
import type { SportsbookLandingPageData } from './templates/SportsbookLandingPage/types.ts';
import type { CRMPromoLandingPagePayload } from './types';

type LandingPagesCRMProps = {
  slug: string;
};

type PromoLandingPageProps = {
  params: Promise<{ slug: string }>;
};

type LandingPagesCRMMetadata = Omit<Metadata, 'title'> & { title: string };

type DataForTemplate = {
  SPORTSBOOK: SportsbookLandingPageData;
  MULTI_OFFER_DIFFERENT_GAMES_CARDS: MultiofferLandingPageData;
  NETWORK_PROMO: NetworkPromoLandingPageData;
};

type TemplateKey = keyof DataForTemplate;

type TemplateLoaderMap = {
  [K in TemplateKey]: () => Promise<React.ComponentType<{ landingPageData: DataForTemplate[K] }>>;
};

const getPromoLandingPageData = cache(getPromoLandingPageDataOriginal);
const TEMPLATE_LOADERS = {
  SPORTSBOOK: () => import('./templates/SportsbookLandingPage/SportsbookLandingPage').then(module => module.SportsbookLandingPage),
  MULTI_OFFER_DIFFERENT_GAMES_CARDS: () => import('./templates/MultiofferLandingPage/MultiofferLandingPage').then(module => module.MultiofferLandingPage),
  NETWORK_PROMO: () => import('./templates/NetworkPromoLandingPage/NetworkPromoLandingPage').then(module => module.NetworkPromoLandingPage),
} satisfies TemplateLoaderMap;

async function LandingPagesCRM({ slug }: LandingPagesCRMProps): Promise<React.JSX.Element | null> {
  const landingPageData = await getPromoLandingPageData(slug).catch(() => notFound());

  const template = landingPageData.list[0]?.template;
  if (!template || !(template in TEMPLATE_LOADERS)) {
    notFound();
  }
  const key = template as TemplateKey;
  const Template = (await TEMPLATE_LOADERS[key]()) as React.ComponentType<{ landingPageData: CRMPromoLandingPagePayload }>;
  return <Template landingPageData={landingPageData} />;
}

async function generateMetadata({ params }: PromoLandingPageProps): Promise<LandingPagesCRMMetadata> {
  const { slug } = await params;

  const landingPageData = await getPromoLandingPageData(slug).catch(() => notFound());
  const { metadata } = landingPageData;

  const robotsArray = metadata.robots?.split(',').map(robot => robot.trim().toLowerCase()) || [];
  const index = !robotsArray.includes('noindex');
  const follow = metadata.inSiteMap && !robotsArray.includes('nofollow');

  return {
    title: metadata.pageTitle,
    description: metadata.description,
    alternates: {
      canonical: toCanonicalPathname(metadata.canonicalPath),
    },
    robots: {
      index,
      follow,
    },
  };
}

export { generateMetadata, LandingPagesCRM };
export type { LandingPagesCRMMetadata, LandingPagesCRMProps, PromoLandingPageProps };
